// Help menu popover — opens from the "?" button.
//
// Lists the overlays available for the current panel. Items are
// hidden when the corresponding data is empty (e.g. no shortcuts =
// no "Keyboard shortcuts" item).

import { useEffect } from "react";
import { useHelp } from "./help-context";
import { getHelpData } from "./help-data";

export function HelpPopover() {
  const { panelKind, overlay, openOverlay, closeOverlay, showWelcome } = useHelp();
  const data = getHelpData(panelKind);

  useEffect(() => {
    if (overlay !== "menu") return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeOverlay();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [overlay, closeOverlay]);

  if (overlay !== "menu") return null;

  return (
    <div
      role="dialog"
      aria-label="Help menu"
      style={{
        position: "absolute",
        top: 56,
        right: 12,
        zIndex: 40,
        background: "var(--white, #ffffff)",
        border: "1px solid var(--rule, #d8d3c7)",
        borderRadius: 10,
        padding: 8,
        minWidth: 240,
        boxShadow: "0 12px 32px rgba(27, 22, 10, 0.18)",
      }}
    >
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {data.welcome && (
          <MenuItem
            label="About this panel"
            sub="Show the welcome card again"
            onClick={showWelcome}
          />
        )}
        {data.hotspots.length > 0 && (
          <MenuItem
            label="Label every control"
            sub="Click annotations on the panel"
            onClick={() => openOverlay("hotspot")}
          />
        )}
        {data.shortcuts.length > 0 && (
          <MenuItem
            label="Keyboard shortcuts"
            sub="Hot-keys for this panel"
            onClick={() => openOverlay("keyboard")}
          />
        )}
        <MenuItem label="Close" sub="" onClick={closeOverlay} />
      </ul>
    </div>
  );
}

function MenuItem({
  label,
  sub,
  onClick,
}: {
  label: string;
  sub: string;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        style={{
          display: "block",
          width: "100%",
          textAlign: "left",
          padding: "8px 10px",
          border: "none",
          background: "transparent",
          borderRadius: 6,
          cursor: "pointer",
          fontSize: 14,
          color: "var(--ink, #1b160a)",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--cream, #fbf3e7)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <div style={{ fontWeight: 600 }}>{label}</div>
        {sub && (
          <div style={{ fontSize: 12, color: "var(--muted, #7a7363)", marginTop: 2 }}>
            {sub}
          </div>
        )}
      </button>
    </li>
  );
}
